import React, { useState, useEffect, useRef } from 'react';
import { XCircle } from 'lucide-react';

import { CustomModal } from '../../components/ui/CustomModal';
import { formatNumber, parseFormattedNumber, getLocalDateString, formatDateForInput, parseAttachmentUrls } from '../../utils/helpers';

export const TransactionModal = ({ isOpen, onClose, onSave, existingTransaction, accounts }) => {
    const initialTransactionState = {
        date: getLocalDateString(),
        type: 'Chi',
        category: 'Ứng tiền PO',
        amount: 0,
        subject: '',
        description: '',
        accountId: accounts.length > 0 ? accounts[0].id : '',
        attachmentUrl: []
    };
    
    const [transaction, setTransaction] = useState(initialTransactionState);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const modalRef = useRef(null);

    const incomeCategories = ["Góp vốn DL", "Góp vốn HCM", "Hoàn tiền PO", "Đối soát Nhanh.vn", "Đối soát Oreka", "Đối soát Lazada", "Đối soát Khác", "Lãi ngân hàng", "Thu khác"];
    const expenseCategories = ["Ứng tiền PO", "Trả lương", "Luân chuyển hàng", "Mua hàng SCGR", "Mua hàng Mộc Hương", "Mua hàng STB", "Mua hàng khác", "Mua hàng NXB", "Đóng thuế", "Chi khác"];

    useEffect(() => {
        const handlePaste = async (event) => {
            if (!event.clipboardData || !event.clipboardData.items) return;
            const items = Array.from(event.clipboardData.items);
            const imageItem = items.find(item => item.type.startsWith('image/'));
            if (imageItem) {
                event.preventDefault();
                const blob = imageItem.getAsFile();
                if (blob) {
                    const file = new File([blob], `pasted-image-${Date.now()}.${blob.type.split('/')[1] || 'png'}`, { type: blob.type });
                    setSelectedFiles(prev => [...prev, file]);
                }
            }
        };

        const modalDiv = modalRef.current;
        if (isOpen && modalDiv) modalDiv.addEventListener('paste', handlePaste);
        return () => { if (modalDiv) modalDiv.removeEventListener('paste', handlePaste); };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            if (existingTransaction) {
                const attachments = parseAttachmentUrls(existingTransaction.attachmentUrl);
                setTransaction({
                    ...existingTransaction,
                    date: formatDateForInput(existingTransaction.date),
                    attachmentUrl: attachments
                });
            } else {
                 setTransaction({...initialTransactionState, date: getLocalDateString(), accountId: accounts.length > 0 ? accounts[0].id : ''});
            }
            setSelectedFiles([]); 
        }
    }, [existingTransaction, isOpen, accounts]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTransaction(p => {
            const updated = { ...p, [name]: value };
            if (name === 'type') {
                updated.category = value === 'Thu' ? incomeCategories[0] : expenseCategories[0];
            }
            return updated;
        });
    };
    
    const handleAmountChange = (e) => {
        const numValue = parseFormattedNumber(e.target.value);
        setTransaction(p => ({...p, amount: numValue}));
    };

    const handleFileChange = (e) => {
        if(e.target.files) {
            setSelectedFiles(prev => [...prev, ...Array.from(e.target.files)]);
        }
    };
    
    const removeFile = (fileToRemove) => {
        setSelectedFiles(prev => prev.filter(f => f !== fileToRemove));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(transaction, selectedFiles, !!existingTransaction);
    };

    return (
        <CustomModal isOpen={isOpen} onClose={onClose} title={existingTransaction ? "Sửa Phiếu" : "Tạo Phiếu Thu/Chi Mới"} maxWidth="max-w-2xl">
            <div ref={modalRef}>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="date" name="date" value={transaction.date} onChange={handleInputChange} className="w-full p-3 border rounded-md" />
                    <select name="type" value={transaction.type} onChange={handleInputChange} className="w-full p-3 border rounded-md bg-white">
                        <option>Chi</option>
                        <option>Thu</option>
                    </select>
                    <select name="category" value={transaction.category} onChange={handleInputChange} className="w-full p-3 border rounded-md bg-white">
                        {(transaction.type === 'Thu' ? incomeCategories : expenseCategories).map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                    <input type="text" name="amount" placeholder="Số tiền" value={formatNumber(transaction.amount)} onChange={handleAmountChange} className="w-full p-3 border rounded-md" required />
                    <select name="accountId" value={transaction.accountId} onChange={handleInputChange} className="w-full p-3 border rounded-md bg-white" required>
                        <option value="">-- Chọn tài khoản thực hiện --</option>
                        {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                    </select>
                    <input type="text" name="subject" placeholder="Đối tượng (VD: Nhà cung cấp A, Khách hàng B)" value={transaction.subject} onChange={handleInputChange} className="w-full p-3 border rounded-md" />
                    <textarea name="description" placeholder="Ghi chú thêm... (Có thể dán ảnh vào đây)" value={transaction.description} onChange={handleInputChange} className="w-full p-3 border rounded-md" />
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tệp đính kèm</label>
                      <div className="flex items-center gap-2">
                        <label htmlFor="file-upload" className="cursor-pointer bg-blue-50 text-blue-700 font-semibold py-2 px-4 rounded-full hover:bg-blue-100">
                            Chọn tệp
                        </label>
                        <input id="file-upload" type="file" multiple onChange={handleFileChange} className="hidden"/>
                        <span className="text-sm text-gray-500">hoặc dán (Ctrl+V) vào bất kỳ đâu trong form.</span>
                      </div>
                      <div className="mt-4 space-y-2">
                          {selectedFiles.map((file, index) => (
                              <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-md">
                                  <span className="text-sm truncate">{file.name}</span>
                                  <button type="button" onClick={() => removeFile(file)} className="text-red-500 hover:text-red-700">
                                      <XCircle size={18} />
                                  </button>
                              </div>
                          ))}
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <button type="button" onClick={onClose} className="py-2 px-5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">Hủy</button>
                        <button type="submit" className="py-2 px-5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Lưu Phiếu</button>
                    </div>
                </form>
            </div>
        </CustomModal>
    );
};